import Vue from 'vue';
import PdfMake from 'pdfmake/build/pdfmake';
import Logo from '@/assets/img/Carle_Logo_BW.png';
import Fonts from './Fonts';

// Array Helpers
export function arrayChunk(arr, chunkSize) {
  if (!arr.length) {
    return [[]];
  }
  const results = [];
  const tmpArr = arr.slice(0);

  while (tmpArr.length) {
    results.push(tmpArr.splice(0, chunkSize));
  }

  return results;
}
export function arraySearch(arr, term, prop, cb = null) {
  let filter = cb;
  if (cb === null) {
    const rgx = new RegExp(term, 'gi');
    filter = (i) => rgx.test(i[prop]);
  }
  return term ? arr.filter(filter) : arr;
}
export function arrayShuffle(arr) {
  const shuffled = [...arr];
  for (let i = arr.length - 1; i > 0; i -= 1) {
    const j = Math.floor(Math.random() * (i + 1));
    [shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]];
  }
  return shuffled;
}
export function arrayRandomItem(arr) {
  return arr[Math.floor(Math.random() * arr.length)];
}
export function arrayToObject(arr, key) {
  const initialValue = {};
  return arr.reduce((obj, item) => ({
    ...obj,
    [item[key]]: item,
  }), initialValue);
}
export const arrayHelpers = {
  arrayChunk,
  arraySearch,
  arrayShuffle,
  arrayRandomItem,
  arrayToObject,
};

// Validation helpers
const emailRegex = '^[a-zA-Z0-9.!#$%&\'*+/=?^_`{|}~-]+@[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?(?:\\.[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?)*$';
const passwordRegex = {
  '(?=^.{6,100}$).*$': 'Password must be 6-100 characters',
  '(?=.*\\d).*$': 'Password must contain at least 1 digit',
  '(?=.*[a-z]).*$': 'Password must contain at least 1 lowercase letter',
  '(?=.*[A-Z]).*$': 'Password must contain at least 1 uppercase letter',
  '(?=.*[!@#$%^&*()_+}{":;\'?/>\\.<;,]).*$': 'Password must contain at least 1 special character',
  '^\\S*$': 'Password must not contain whitespace',
};
export const rules = {
  required: (v, msg) => !!v || msg,
  password: (v) => {
    let msg = 'Password is not secure';
    return Object.keys(passwordRegex).every((k) => {
      msg = passwordRegex[k];
      return new RegExp(k).test(v);
    }) || msg;
  },
  email: (v, msg) => (!v || new RegExp(emailRegex).test(v)) || msg,
  pattern: (v, pattern, msg) => (!v || pattern.test(v)) || msg,
  match: (v, compare, msg) => (v === compare) || msg,
  length: (v, len, msg, exact = false) => (exact ? v.length === len : v.length <= len) || msg,
};
export const validationHelpers = {
  emailRegex,
  passwordRegex,
  rules,
};

// Media Helpers
export function getMediaUrl(id, directory = '', notfound = null) {
  const base = Vue.prototype.$http.defaults.baseURL;
  const dir = directory ? `${directory}/` : '';
  const fallback = notfound ? `?notfound=${notfound}` : '';
  return `${base}/Media/${dir}${id}${fallback}`;
}
export function getMediaImageData(url, w = 250, h = w) {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.setAttribute('crossorigin', 'anonymous');
    img.onload = () => {
      const canvas = document.createElement('canvas');
      canvas.width = w;
      canvas.height = h;

      const scale = Math.max(w / img.width, h / img.height);
      const scaleX = (w / 2) - (img.width / 2) * scale;
      const scaleY = (h / 2) - (img.height / 2) * scale;
      const ctx = canvas.getContext('2d');
      ctx.drawImage(img, scaleX, scaleY, img.width * scale, img.height * scale);
      resolve(canvas.toDataURL('image/png'));
    };
    img.onerror = (e) => {
      reject(e);
    };
    img.src = url;
  });
}
export async function generateProviderPdf(providerId, provider) {
  PdfMake.vfs = Fonts;
  PdfMake.fonts = {
    AvenirNext: {
      normal: 'AvenirNext-Regular.ttf',
      bold: 'AvenirNext-Medium.ttf',
      italics: 'AvenirNext-Italic.ttf',
      bolditalics: 'AvenirNext-BoldItalic.ttf',
    },
  };

  const bodyMargins = [15, 10, 15, 0];
  const url = provider.PhotoId
    ? getMediaUrl(provider.PhotoId)
    : getMediaUrl(0, 'Provider', 'Provider.jpg');
  const image = await getMediaImageData(url, 360, 360);
  const footerLogo = await getMediaImageData(Logo, 500, 135);

  const content = [
    {
      style: 'header',
      table: {
        widths: [140, 260],
        body: [
          [
            {
              image,
              width: 130,
            },
            {
              stack: [
                { text: `${provider.FirstName} ${provider.LastName}, ${provider.Title}`, style: 'h1' },
                { text: provider.Specialties.length ? `${provider.Specialties[0]}` : '', style: 'p' },
              ],
              style: 'headerText',
            },
          ],
        ],
      },
      layout: 'noBorders',
    },
  ];

  // Trainng and History
  if (provider.Education.length) {
    content.push([
      {
        text: 'Education'.toUpperCase(),
        style: 'p',
        margin: bodyMargins,
        headlineLevel: 1,
      },
      {
        ul: provider.Education.map((h) => [
          `${h.Institute ? `${h.Institute.Title}: ` : ''}${h.Focus} - ${h.Years}`,
        ]),
        margin: bodyMargins,
      },
    ]);
  }
  if (provider.Fellowships.length) {
    content.push([
      {
        text: 'Fellowships'.toUpperCase(),
        style: 'p',
        margin: bodyMargins,
        headlineLevel: 1,
      },
      {
        ul: provider.Fellowships.map((h) => [
          `${h.Institute ? `${h.Institute.Title}: ` : ''}${h.Focus} - ${h.Years}`,
        ]),
        margin: bodyMargins,
      },
    ]);
  }
  if (provider.Internships.length) {
    content.push([
      {
        text: 'Internships'.toUpperCase(),
        style: 'p',
        margin: bodyMargins,
        headlineLevel: 1,
      },
      {
        ul: provider.Internships.map((h) => [
          `${h.Institute ? `${h.Institute.Title}: ` : ''}${h.Focus} - ${h.Years}`,
        ]),
        margin: bodyMargins,
      },
    ]);
  }
  if (provider.Residencies.length) {
    content.push([
      {
        text: 'Residencies'.toUpperCase(),
        style: 'p',
        margin: bodyMargins,
        headlineLevel: 1,
      },
      {
        ul: provider.Residencies.map((h) => [
          `${h.Institute ? `${h.Institute.Title}: ` : ''}${h.Focus} - ${h.Years}`,
        ]),
        margin: bodyMargins,
      },
    ]);
  }
  if (provider.Publications.length) {
    content.push([
      {
        text: 'Publications'.toUpperCase(),
        style: 'p',
        margin: bodyMargins,
        headlineLevel: 1,
      },
      {
        ul: [...provider.Publications],
        margin: bodyMargins,
      },
    ]);
  }

  // Tags
  if (provider.Affiliations.length) {
    content.push([
      {
        text: 'Affiliations'.toUpperCase(),
        style: 'p',
        margin: bodyMargins,
        headlineLevel: 1,
      },
      {
        ul: [...provider.Affiliations],
        margin: bodyMargins,
      },
    ]);
  }
  if (provider.Certifications.length) {
    content.push([
      {
        text: 'Certifications'.toUpperCase(),
        style: 'p',
        margin: bodyMargins,
        headlineLevel: 1,
      },
      {
        ul: [...provider.Certifications],
        margin: bodyMargins,
      },
    ]);
  }
  if (provider.Departments.length) {
    content.push([
      {
        text: 'Departments'.toUpperCase(),
        style: 'p',
        margin: bodyMargins,
        headlineLevel: 1,
      },
      {
        ul: [...provider.Departments],
        margin: bodyMargins,
      },
    ]);
  }
  if (provider.ResearchDepartments.length) {
    content.push([
      {
        text: 'Research Departments'.toUpperCase(),
        style: 'p',
        margin: bodyMargins,
        headlineLevel: 1,
      },
      {
        ul: [...provider.ResearchDepartments],
        margin: bodyMargins,
      },
    ]);
  }
  if (provider.ResearchInterests.length) {
    content.push([
      {
        text: 'Research Interests'.toUpperCase(),
        style: 'p',
        margin: bodyMargins,
        headlineLevel: 1,
      },
      {
        ul: [...provider.ResearchInterests],
        margin: bodyMargins,
      },
    ]);
  }
  if (provider.Specialties.length) {
    content.push([
      {
        text: 'Specialties'.toUpperCase(),
        style: 'p',
        margin: bodyMargins,
        headlineLevel: 1,
      },
      {
        ul: [...provider.Specialties],
        margin: bodyMargins,
      },
    ]);
  }
  if (provider.MedicalInterests.length) {
    content.push([
      {
        text: 'Medical Interests'.toUpperCase(),
        style: 'p',
        margin: bodyMargins,
        headlineLevel: 1,
      },
      {
        ul: [...provider.MedicalInterests],
        margin: bodyMargins,
      },
    ]);
  }
  if (provider.AdditionalRecognitions.length) {
    content.push([
      {
        text: 'Additional Recognitions'.toUpperCase(),
        style: 'p',
        margin: bodyMargins,
        headlineLevel: 1,
      },
      {
        ul: [...provider.AdditionalRecognitions],
        margin: bodyMargins,
      },
    ]);
  }

  return new Promise((resolve, reject) => {
    try {
      PdfMake.createPdf({
        defaultStyle: {
          font: 'AvenirNext',
        },
        pageMargins: [0, 0, 0, 180],
        pageSize: {
          width: 400,
          height: 980,
        },
        styles: {
          header: {
            fontSize: 16,
            color: '#414142',
            fillColor: '#F1F1F1',
            margin: [0, 0, 20, 0],
          },
          headerText: {
            tableCellPadding: [10, 30, 10, 30],
            margin: [10, 30, 10, 30],
          },
          h1: {
            fontSize: 18,
            bold: true,
          },
          p: {
            fontSize: 16,
          },
          footerPrepend: {
            fontSize: 16,
            italics: true,
          },
          footer: {
            fontSize: 14,
            lineHeight: 0.8,
            bold: true,
          },
          footerAppend: {
            fontSize: 9,
          },
        },
        content,
        footer(currentPage, pageCount) {
          return [
            {
              text: currentPage < pageCount ? 'Continued on back.' : '',
              style: 'footerPrepend',
              margin: [15, 40, 15, 40],
            },
            {
              columns: [
                {
                  alignment: 'left',
                  stack: [
                    'For more information, or to schedule an appointment, call',
                    '(217) 383-3080 or visit carle.org',
                  ],
                  style: 'footer',
                },
                {
                  alignment: 'right',
                  image: footerLogo,
                  width: 150,
                },
              ],
              columnGap: 10,
              margin: [15, 0, 15, 0],
            },
            {
              alignment: 'right',
              text: 'carle.org | 611 W. Park Street, Urbana, IL 61801 | (217) 383-3080',
              style: 'footerAppend',
              margin: [15, 10, 15, 0],
            },
          ];
        },
        pageBreakBefore(currentNode, followingNodes) {
          const nextNodes = followingNodes.filter((n) => n.stack === false);
          const hasNext = nextNodes.length > 0 && nextNodes[0].text !== 'Continued on back.';
          return currentNode.headlineLevel === 1 && !hasNext;
        },
      }).getBlob(async (blob) => {
        const data = new FormData();
        data.append(
          'file',
          blob,
          `${provider.LastName.toLowerCase()}-${provider.FirstName.toLowerCase()}-${providerId}.pdf`,
        );
        const response = await Vue.prototype.$http.post('Media/PDF', data, {
          headers: {
            'Content-Type': 'multipart/form-data',
          },
        });
        resolve(response);
      });
    } catch (e) {
      reject(e);
    }
  });
}
export const mediaHelpers = {
  getMediaUrl,
  getMediaImageData,
  generateProviderPdf,
};

export default {
  ...arrayHelpers,
  ...validationHelpers,
  ...mediaHelpers,
};
